import {ElementRef, inject, Injectable, Injector, runInInjectionContext} from '@angular/core';
import {Functions, httpsCallable}                                        from '@angular/fire/functions';
import {ref, Storage, uploadBytes}                                       from '@angular/fire/storage';
import {FoodOutgoingIntf, FoodRecordLogIntf}                             from '@djsharkk/logr-types';
import {UiNotificationsService}                                          from '../../../../../../utils/ui-notifications.service';
import {MenuCrudService}                                                 from '../../../../../utils/menu-crud.service';

@Injectable({
				providedIn: 'root'
			})
export class MenuCrudPanelFoodService {
	svcUiNotifications = inject(UiNotificationsService)
	svcMenuCrud = inject(MenuCrudService)
	injector = inject(Injector)


	async processImage(refVideo: ElementRef<HTMLVideoElement>, refCanvas: ElementRef<HTMLCanvasElement>) {
		this.svcMenuCrud.crudMenuFoodMode = 'processing'
		if (!refVideo || !refCanvas) {
			console.error('Video or canvas element is missing');
			this.svcMenuCrud.crudMenuFoodMode = null;
			return;
		}


		const videoElement = refVideo.nativeElement;
		const canvasElement = refCanvas.nativeElement;

		// Set canvas dimensions to match the video stream
		canvasElement.width = videoElement.videoWidth;
		canvasElement.height = videoElement.videoHeight;

		// Draw the current video frame onto the canvas
		const context = canvasElement.getContext('2d');
		if (!context) {
			console.error('Failed to get canvas context');
			return;
		}

		context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);


		// Convert the canvas content to a Blob (binary data)
		try {
			const uploadImage: Promise<Blob | null> = new Promise<Blob | null>((resolve) => {
				canvasElement.toBlob((blob) => resolve(blob), 'image/png');
			});
			return await uploadImage;
		} catch (error) {
			console.error('Failed to convert canvas to Blob');
			return;
		}
	}

	async captureUpload(imageBlob: Blob): Promise<FoodRecordLogIntf> {
		return await runInInjectionContext(this.injector, async () => {
			const storage = inject(Storage);

			const fileName = `food_records_log/${Date.now()}.png`;
			const storageRef = ref(storage, fileName);

			try {
				const uploadResult = await uploadBytes(storageRef, imageBlob);
				console.log('✅ Image uploaded:', uploadResult);

				// const buffer = await imageBlob.arrayBuffer();
				const base64 = await this.blobToBase64(imageBlob);

				const result = await this.callFirebaseFunction({
																   base: base64,
																   meta: uploadResult.metadata
															   });

				console.log('✅ Callable function response:', result.data);

				this.svcMenuCrud.crudMenuFoodMode = 'reviewing';
				return result.data;
			} catch (error) {
				console.error('❌ Error in captureUpload:', error);
				this.svcUiNotifications.showNotification('Upload failed');
				this.svcMenuCrud.crudMenuFoodMode = 'awaiting';
				throw error;
			}
		});
	}

	private async blobToBase64(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}

	private async callFirebaseFunction(pack: FoodOutgoingIntf) {
		return await runInInjectionContext(this.injector, async () => { // Ensure the result is returned
			const functions = inject(Functions);
			try {
				const callable = httpsCallable<FoodOutgoingIntf, FoodRecordLogIntf>(functions, 'analyzeFoodImage');
				return await callable(pack); // This return value will now propagate properly
			} catch (error) {
				console.error('Error calling analyzeFoodImage function:', error);
				throw error; // Re-throw the error to ensure the method fails if the call fails
			}
		});
	}

	private quantizeGray(value: number, levels: number): number {
		const step = 256 / levels;
		return Math.floor(value / step) * step;
	}


	draw(refVideo: ElementRef<HTMLVideoElement>, refCanvas: ElementRef<HTMLCanvasElement>): void {
		const video = refVideo.nativeElement;
		const canvas = refCanvas.nativeElement;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const scale = this.svcMenuCrud.crudMenuFoodMode ? 0.4 : 0.2; // downscale for pixelation
		const grayLevels = 8; // 8 grayscale shades (you can change this)

		const renderFrame = () => {
			const w = video.videoWidth;
			const h = video.videoHeight;

			canvas.width = w * scale;
			canvas.height = h * scale;

			if (!canvas.width || !canvas.height) {
				requestAnimationFrame(renderFrame); // skip this frame, wait until dimensions are valid
				return;
			}

			ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
			const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
			const data = frame.data;

			for (let i = 0; i < data.length; i += 4) {
				const r = data[i];
				const g = data[i + 1];
				const b = data[i + 2];

				// Weighted grayscale formula for more accurate brightness
				const gray = this.quantizeGray(0.3 * r + 0.59 * g + 0.11 * b, grayLevels);

				data[i] = data[i + 1] = data[i + 2] = gray;
			}

			ctx.putImageData(frame, 0, 0);
			requestAnimationFrame(renderFrame);
		};

		renderFrame();
	}
}
